import _ from "lodash";
import moment from "moment";

export const TIME_OFFSET_DAY_END = { hours: 23, minutes: 59, seconds: 59 };

type FormattedDateArgs = {
	date: moment.MomentInput;
	format?: string;
	keepLocalTime?: boolean;
};

export const getFormattedDate = ({
	date,
	format = "MM/DD/YYYY h:mm A",
	// if we set keepLocalTime to "true" we`ll prevent such bugs as SUP-476
	keepLocalTime = false,
}: FormattedDateArgs): string => {
	if (!date) return "";
	return moment.utc(date).local(keepLocalTime).format(format);
};

type ConvertProps = {
	addTime?: { [key: string]: number };
};

export const convertDateToISO = (
	date?: Date | string,
	properties?: ConvertProps,
) => {
	if (!date) return;

	const convertDateToISOString = dateResult => {
		if (typeof dateResult === "string") {
			const dateComponents = dateResult.split("/");
			const [month, day, year] = dateComponents.map(Number);

			const date = new Date(Date.UTC(year, month - 1, day, 21, 0, 0));
			const previousDay = new Date(date.getTime() - 24 * 60 * 60 * 1000); // Subtract 24 hours to get the previous day.

			return previousDay.toISOString();
		} else {
			return dateResult.toISOString();
		}
	};

	return properties?.addTime
		? moment(date).add(properties.addTime).toISOString()
		: convertDateToISOString(date);
};

export const getDuration = ({
	start,
	end,
}: {
	start: Date;
	end?: Date;
}): string => {
	if (!start) return "";
	return start && end
		? moment(start).from(end, true)
		: moment.utc(start).local().fromNow(true);
};

export const getDateWithDuration = ({
	start,
	end,
	separator = "/",
	format,
}: {
	start: Date;
	end?: Date;
	separator?: string;
	format?: string;
}): string => {
	const date = getFormattedDate({ date: start, format });
	const duration = getDuration({ start, end });
	return `${date} ${separator} ${duration}`;
};

export const convertDatesToLocal = (result: string) => {
	if (!result) {
		return result;
	}

	let dateTimeRegex = new RegExp(
		"([0-9]|0[0-9]|1[0-9])/([0-9][0-9]|[0-9])/[0-9]{4} ([0-9]|0[0-9]|1[0-9]):[0-9]{2}:[0-9]{2} (AM|PM)",
		"g",
	);
	let utcDates = _.uniqBy(result.match(dateTimeRegex), date => date);

	let newResult = result;
	for (let i = 0; i < utcDates.length; i++) {
		newResult = newResult.replaceAll(
			utcDates[i],
			moment.utc(utcDates[i]).local().format("MM/DD/YYYY h:mm A"),
		);
	}

	return newResult;
};

export const timeZoneOffsetInMinutes = () => {
	return moment().utcOffset();
};

export const parseStringToDate = (
	date: Date | string | null | undefined,
	format?: string,
): Date => {
	const convertedDate = moment(date).toDate();

	return isNaN(convertedDate.valueOf()) && format
		? moment(date, format).toDate()
		: convertedDate;
};

export const getFormattedExpirationDate = (month, year) => {
	if (!month || !year) return;
	const formattedYear = moment(year, "YYYY").format("YY");
	const formattedMonth = moment(month, "M").format("MM");
	return `${formattedMonth}/${formattedYear}`;
};

export const getDateWithoutTime = (date: Date) => {
	if (!date) return;
	return moment(date)
		.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
		.format("YYYY-MM-DDTHH:mm:ss");
};

export const getOnlyDate = (date: Date) => {
	if (!date) return;
	return moment(date).format("YYYY-MM-DD");
};
// Convert seconds to a human-readable duration
export const getDurationFromSeconds = (seconds: number = 0) => {
	if (!seconds) return;

	const duration = moment.duration(seconds, "seconds");
	const futureTime = moment().add(duration);

	return futureTime.fromNow(true);
};
